import React, { useRef, useState } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { ImageUp, Upload, X } from 'lucide-react';
import { ALLOWED_PRODUCT_IMAGE_CONTENT_TYPES, ProductMediaClient } from '@/infrastructure/ProductMediaClient';

interface FeaturedImageEditorProps {
  sku: string;
  /** Called with the published URL so the row can show the new image without a full refetch. */
  onPublished?: (wpFeaturedImageUrl: string) => void;
  client?: ProductMediaClient;
}

type PublishState =
  | { status: 'idle' }
  | { status: 'uploading' }
  | { status: 'publishing' }
  | { status: 'published'; message: string }
  | { status: 'failed'; message: string };

/**
 * Replaces a product's featured image, so the category team does not have to open WP Admin — which
 * cannot clear the product cache and can overwrite an existing file in the bucket (SSD-6152).
 *
 * Two ways in. A pasted URL is handed straight to the endpoint and WordPress downloads it itself. A
 * chosen file is uploaded to the product image bucket first, because WordPress can only fetch a
 * URL; that path mints a key with a random suffix, so it cannot collide with an existing object.
 */
export const FeaturedImageEditor: React.FC<FeaturedImageEditorProps> = ({ sku, onPublished, client }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [publishState, setPublishState] = useState<PublishState>({ status: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mediaClient = client ?? new ProductMediaClient();
  const isBusy = publishState.status === 'uploading' || publishState.status === 'publishing';
  const trimmedImageUrl = imageUrl.trim();
  const hasSomethingToPublish = Boolean(selectedFile) || trimmedImageUrl.length > 0;

  const chooseFile = (file: File | null) => {
    setSelectedFile(file);
    // The two inputs are alternatives, so picking one clears the other rather than leaving the user
    // guessing which the button will use.
    if (file) {
      setImageUrl('');
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const publish = async () => {
    if (!hasSomethingToPublish || isBusy) {
      return;
    }

    try {
      let sourceUrl = trimmedImageUrl;
      if (selectedFile) {
        setPublishState({ status: 'uploading' });
        sourceUrl = await mediaClient.uploadImage(selectedFile);
      }

      setPublishState({ status: 'publishing' });
      const response = await mediaClient.publishFeaturedThumbnail({ sku, imageUrl: sourceUrl });

      setPublishState({ status: 'published', message: 'Featured image updated. The gallery is unchanged.' });
      setImageUrl('');
      clearFile();
      onPublished?.(response.wpFeaturedImageUrl);
    } catch (error) {
      setPublishState({
        status: 'failed',
        message: error instanceof Error ? error.message : 'Failed to publish featured image',
      });
    }
  };

  const buttonLabel =
    publishState.status === 'uploading'
      ? 'Uploading…'
      : publishState.status === 'publishing'
        ? 'Publishing…'
        : 'Publish';

  return (
    <div className="col-md-12">
      <Form.Label
        style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          display: 'block',
          marginBottom: '0.5rem',
          marginTop: '0.75rem',
        }}
      >
        Featured image
      </Form.Label>

      <div className="d-flex gap-2 align-items-center">
        <Form.Control
          size="sm"
          type="url"
          placeholder="Paste an image URL, or choose a file"
          aria-label={`Featured image URL for ${sku}`}
          value={imageUrl}
          disabled={isBusy || Boolean(selectedFile)}
          onChange={(event) => setImageUrl(event.target.value)}
        />
        <Button size="sm" variant="outline-secondary" disabled={isBusy} onClick={() => fileInputRef.current?.click()}>
          <Upload size={14} />
          <span className="ms-1">Choose file</span>
        </Button>
        <Button size="sm" variant="outline-primary" disabled={!hasSomethingToPublish || isBusy} onClick={publish}>
          {isBusy ? <Spinner animation="border" size="sm" role="status" aria-hidden="true" /> : <ImageUp size={14} />}
          <span className="ms-1">{buttonLabel}</span>
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="d-none"
        aria-label={`Featured image file for ${sku}`}
        accept={ALLOWED_PRODUCT_IMAGE_CONTENT_TYPES.join(',')}
        onChange={(event) => chooseFile(event.target.files?.[0] ?? null)}
      />

      {selectedFile && (
        <div className="d-flex align-items-center gap-2 mt-1" style={{ fontSize: '0.7rem' }}>
          <span className="text-muted">{selectedFile.name}</span>
          <Button
            size="sm"
            variant="link"
            className="p-0 text-danger"
            disabled={isBusy}
            onClick={clearFile}
            aria-label="Remove selected file"
          >
            <X size={12} />
          </Button>
        </div>
      )}

      {publishState.status === 'published' && (
        <div className="text-success mt-1" style={{ fontSize: '0.7rem' }} role="status">
          {publishState.message}
        </div>
      )}
      {publishState.status === 'failed' && (
        <div className="text-danger mt-1" style={{ fontSize: '0.7rem' }} role="alert">
          {publishState.message}
        </div>
      )}
    </div>
  );
};
